/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Input,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import size from 'lodash/size';

import { withTheme } from 'styled-components';
import { StyledEngineProvider } from '@mui/material/styles';
import AuthFormWrapper from './AuthFormWrapper';

import appTheme from '../common/theme/appTheme.json';
import { Authorities } from '../../shared/entities/user';
import { Email } from '@mui/icons-material';
import { AccessToken, IDToken, Tokens } from '@okta/okta-auth-js';
import { useDispatch } from 'react-redux';
import { setAuthTokens } from '../../store/session/actions';
import { useHistory } from 'react-router-dom';
import BeamTooltip from '../common/beam/BeamTooltip';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const AuthForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [email, setEmail] = useState<string>('mark.dankberg@viasat.com');
  const [authorities, setAuthorities] = useState<Authorities[]>([
    // Default to having all authorities - devs can deselect as-needed.
    ...Object.values(Authorities).filter(k => typeof k === 'string'),
  ]);
  const [rememberMe, setRememberMe] = useState(true);

  const handleAuthoritiesChange = (event: SelectChangeEvent<typeof authorities>) => {
    const {
      target: { value },
    } = event;
    setAuthorities(
      // On autofill we get a stringified value.
      (typeof value === 'string' ? value.split(',') : value) as Authorities[],
    );
  };

  const onSubmitFn = () => {
    // Create a fake access & ID token
    const accessToken: AccessToken = {
      accessToken: 'my token',
      claims: {
        sub: "doesn't matter",
        email,
        authorities,
        name: "Dankberg, Mark",
      },
      tokenType: 'accessToken',
      userinfoUrl: "doesn't matter",
      expiresAt: 0,
      authorizeUrl: "doesn't matter",
      scopes: ["doesn't matter"],
    };

    const idToken: IDToken = {
      ...accessToken,

      idToken: 'my id token',
      issuer: '',
      clientId: '',
    };

    const tokens: Tokens = {
      accessToken,
      idToken,
    };

    // Save tokens to local storage
    if (rememberMe) {
      localStorage.setItem('mockTokens', JSON.stringify(tokens));
    }

    dispatch(setAuthTokens(tokens));
    history.push('/');
  };

  // Add entry to <Select /> for each defined authority
  let authoritiesSelectItems = [];
  for (const entry in Authorities) {
    const authorityString = Authorities[entry];
    authoritiesSelectItems.push(
      <MenuItem key={authorityString} value={authorityString}>
        <Checkbox checked={authorities.indexOf(authorityString as Authorities) > -1} />
        <ListItemText primary={authorityString} />
      </MenuItem>,
    );
  }

  return (
    <AuthFormWrapper
      className="auth__form"
      onSubmit={(e: React.FormEvent) => {
        onSubmitFn();
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="auth__title">
        <span className="auth__title__viasat">Viasat</span>
        <span className="auth__title__insights">Unified Portal</span>
      </div>

      <FormControl sx={{ width: 280 }}>
        <InputLabel>Email</InputLabel>
        <Input
          className="auth__textfield"
          margin="none"
          value={email}
          id="email"
          name="email"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(e.target.value);
          }}
          placeholder="Email"
          fullWidth
          disableUnderline
          autoFocus
          startAdornment={
            <InputAdornment className="input__adornment" position="start">
              <Email />
            </InputAdornment>
          }
        />
      </FormControl>

      <FormControl sx={{ width: 280 }}>
        <InputLabel>Authorities</InputLabel>
        <Select
          multiple
          value={authorities}
          onChange={handleAuthoritiesChange}
          input={<OutlinedInput label="Tag" />}
          renderValue={selected => (selected.length === 1 ? selected.join('') : `${selected.length} roles selected`)}
          MenuProps={MenuProps}
          sx={{
            color: 'white',
          }}
        >
          {authoritiesSelectItems}
        </Select>
      </FormControl>

      <BeamTooltip
        title={
          "Simulate a login session. When you close and re-open UP, you'll automatically be authenticated. Log out to clear the session."
        }
        placement={'right'}
      >
        <FormControlLabel
          control={<Checkbox checked={rememberMe} onChange={e => setRememberMe(!rememberMe)} />}
          label="Remember me?"
        />
      </BeamTooltip>

      <div className="auth__actions">
        <StyledEngineProvider injectFirst>
          <Button
            color="primary"
            type="submit"
            id="auth__button"
            disabled={!size(email)}
            sx={{
              height: '46px',
              width: '280px',
              borderRadius: '4px',
              fontWeight: 'bold',
              lineHeight: '16px',
              letterSpacing: '1.15px',
              backgroundColor: appTheme.buttonSelectedColor,
              color: `#1e3640 !important`,
              ':disabled': {
                color: `white !important`,
                backgroundColor: appTheme.loginButtonColor,
              },
              ':hover': {
                backgroundColor: appTheme.buttonSelectedColor,
              },
            }}
          >
            <span className="auth__button__text">Login</span>
          </Button>
        </StyledEngineProvider>
      </div>
    </AuthFormWrapper>
  );
};

export default withTheme(AuthForm);
